/**
 * The pivot's LAYOUT: which rows print, in what order, and what each cell holds.
 *
 * `pivot()` answers "what is the number at this coordinate". This answers the
 * other half — where the coordinates go. Row groups close in a particular
 * order, subtotal rows land between the leaf rows they cover, a repeated row
 * header is blanked, and a total that means nothing prints nothing.
 *
 * It lives HERE, in a package with no React in it, because there are two
 * consumers and only one layout. `PivotTable` draws it; the Excel export walks
 * it emitting `SUBTOTAL` formulas over the cells beneath each total, which it
 * can only do if it knows exactly which rows those are. Written twice, the two
 * walks agree until the day they don't, and the tell is an exported file that
 * quietly stops matching the screen it came from — see
 * docs/reporting/excel-export.md.
 *
 * NOTHING HERE FORMATS. Values leave as numbers and labels as the model's own
 * text; an em dash, a thousands separator and a percent sign are decisions
 * about a medium, and the two media disagree (a spreadsheet wants a blank cell
 * where the screen wants "—", or the column stops being a number).
 */
import { findDimension, type ReportModel } from './model';
import {
  type DimTuple,
  type DimValue,
  dimText,
  type PivotView,
  type TotalPosition,
  totalIsMeaningful,
  tupleKey,
} from './result';
import type { ReportSpec } from './spec';

/**
 * One column of the value area, left to right.
 *
 * The row-header columns are NOT here: there are `spec.rows.length` of them,
 * they carry text rather than measures, and every consumer treats them as a
 * separate block. {@link PivotRow.head} is their half.
 */
export type PivotColumn = {
  measureId: string;
  /** The first measure of its band — where the group border is drawn. */
  bandStart: boolean;
} & (
  | { isTotalBand: false; colKey: DimTuple }
  // A discriminated union rather than an optional `colKey`, so the one column
  // kind that HAS no tuple cannot be read as though it did.
  | { isTotalBand: true; colKey?: undefined }
);

/** One cell of a nested column header, spanning the columns beneath it. */
export interface PivotHeaderCell {
  /** Full ancestor path, which is what decides whether to widen or start anew. */
  path: string;
  /** Index of the first column this cell covers. Disambiguates a React key. */
  start: number;
  member: string;
  colSpan: number;
}

/** One level of the nested column header — one per column dimension. */
export interface PivotHeaderLevel {
  dimensionId: string;
  cells: PivotHeaderCell[];
}

/** One row-header cell: what prints at one level of one row. */
export interface PivotRowHead {
  /** What prints. Already blank for a suppressed repeat and for the columns a
   *  subtotal caption does not occupy. */
  text: string;
  /**
   * A dimension value carrying neither label nor key.
   *
   * Distinct from an empty `text` that was blanked on purpose: a medium with a
   * way to say "no value" (the screen's em dash) uses it here and nowhere else.
   */
  empty: boolean;
  /** Blanked because it repeats the row above (`spec.suppressRepeats`). */
  repeat: boolean;
  /** The dimension key at this level, where this cell names one. */
  key?: string | null;
  /**
   * The group this cell OPENS, where it opens one a reader can fold.
   *
   * Set on exactly one visible cell per collapsible group — the first row of it
   * that prints — so a group has one control however many rows it spans, and a
   * repeated label further down does not grow a second. `collapsed` is the
   * state the control toggles away from.
   */
  toggle?: { key: string; collapsed: boolean };
}

/** One value-area cell. */
export interface PivotCell {
  measureId: string;
  /** The DISPLAY value — `showAs` already applied, as `pivot()` returns it. */
  value: number | null;
  /** Where this total sits. Absent on a plain value cell. */
  position?: TotalPosition;
  /**
   * Whether this cell may print at all.
   *
   * Always true for a value cell. False for a total that is not a quantity —
   * a ratio's, or a percentage totalled across the axis it was not computed
   * over. `totalIsMeaningful` is the rule; this is its answer, resolved once
   * so neither consumer re-derives it.
   */
  meaningful: boolean;
}

/**
 * What a body row is.
 *
 * `value` is one row tuple. `subtotal` closes one group — and is also what a
 * COLLAPSED group prints as (`collapsed: true`), because a folded group is its
 * own total standing where the group was. `total` is the single bottom row, and
 * there is at most one.
 */
export type PivotRowKind = 'value' | 'subtotal' | 'total';

export interface PivotRow {
  kind: PivotRowKind;
  /** One entry per row dimension, left to right. */
  head: PivotRowHead[];
  /** One entry per {@link PivotLayout.columns}, in that order. */
  cells: PivotCell[];
  /** `value`: the row tuple. `subtotal`: the group prefix. `total`: absent. */
  key?: DimTuple;
  /** `subtotal` only: which row level closed here. */
  level?: number;
  /**
   * TOTALS ONLY: index into {@link PivotLayout.rows} of the first row this
   * total covers. It runs to the row before this one.
   *
   * The renderer has no use for it; the spreadsheet writer cannot work without
   * it, because a live total is a formula over a RANGE and the range is exactly
   * this span. Computed here because it is a fact about the layout — which rows
   * a total is the total OF — and re-deriving it from the emitted grid means
   * re-implementing the group walk in the one place that must not have a second
   * copy of it.
   */
  from?: number;
  /**
   * `subtotal` only: this row is a folded group, not a band beneath an open
   * one. Its caption is the group's own name rather than "… total", and it
   * carries the group's toggle.
   */
  collapsed?: boolean;
  /**
   * Inside a collapsed group, so not on screen.
   *
   * EMITTED ANYWAY, rather than dropped. The spreadsheet writes these rows as
   * hidden outline rows — so the reader can unfold them in Excel, and so the
   * folded group's total is still a formula over the rows it covers. Dropping
   * them would make `from` point at rows that are not there.
   */
  hidden?: boolean;
  /**
   * How many enclosing groups have a total row of their own (a subtotal, or a
   * collapsed caption) — the row's Excel outline level. A group with no total
   * row has nothing for an outline button to sit on, so it does not count.
   */
  outline: number;
}

export interface PivotLayout {
  /** `spec.rows` — one row-header column each, left of {@link columns}. */
  rowDimensions: readonly string[];
  /** Nested column header, outermost level first. Empty with no column axis. */
  headerLevels: PivotHeaderLevel[];
  /** The value area's columns, left to right. */
  columns: PivotColumn[];
  /** Leaf rows, subtotal rows and the bottom total row, in printing order. */
  rows: PivotRow[];
  /** Is there a trailing total band? Needs a column axis to total across. */
  showColTotal: boolean;
  /** Is there a bottom total row? */
  showRowTotal: boolean;
  /**
   * Is this grid over PART of the answer?
   *
   * `PivotLayoutOptions.truncated`, kept on the layout so that everything
   * describing this grid reads ONE value. The caption below used it and the
   * workbook's `About` sheet tested `meta.truncated` instead, which is a
   * different condition — so a file could carry a row labelled
   * "Total (partial)" and no sentence anywhere saying why.
   */
  partial: boolean;
}

export interface PivotLayoutOptions {
  /**
   * `ResultMeta.truncated` — the query stopped at its group ceiling.
   *
   * Reaches the LAYOUT rather than each renderer because the bottom row is
   * where the damage shows: a figure labelled "Total" that is the total of
   * whatever came back. Naming it here renames it on the screen and in the
   * exported workbook at once, which is the same reason the export walks this
   * function instead of a layout of its own.
   */
  truncated?: boolean;
}

/**
 * Lay out one pivot.
 *
 * Pure, and cheap enough to run per render: it calls `view` once per cell it
 * emits and does no work per cell it doesn't.
 */
/**
 * A member's caption, with the ABSENCE of a key named rather than drawn as a
 * dash.
 *
 * A null key is a real group: work with no customer (a draft still being
 * written), an employee with no Sr. Controller — 14,746 hours of one on
 * production. `dimText` renders it `''`, which the table then prints as the
 * same em dash it uses for "no value here", so a legitimate category and a
 * hole in the data arrive looking identical. This says which it is, in the
 * dimension's own words, so "— No Customer —" and "— No Sr. Controller —"
 * read as the categories they are.
 *
 * Only for a NULL key. A key that exists with a blank label is a different
 * thing — a name nobody filled in — and still prints as the dash.
 */
function memberText(
  model: ReportModel,
  dimensionId: string | undefined,
  value: DimValue | undefined,
): string {
  if (!value || value.key !== null) return dimText(value);
  const label = (dimensionId ? findDimension(model, dimensionId)?.label : undefined) ?? 'value';
  return `— No ${label} —`;
}

export function pivotLayout(
  model: ReportModel,
  spec: ReportSpec,
  view: PivotView,
  options: PivotLayoutOptions = {},
): PivotLayout {
  const measures = spec.measures;
  // "Total across the columns" needs a column axis to total across.
  const showColTotal = spec.totals.col && spec.cols.length > 0;
  const showRowTotal = spec.totals.row;

  const columns: PivotColumn[] = [
    ...view.colKeys.flatMap((colKey) =>
      measures.map(
        (measureId, mi): PivotColumn => ({
          isTotalBand: false,
          colKey,
          measureId,
          bandStart: mi === 0,
        }),
      ),
    ),
    ...(showColTotal
      ? measures.map(
          (measureId, mi): PivotColumn => ({
            isTotalBand: true,
            measureId,
            bandStart: mi === 0,
          }),
        )
      : []),
  ];

  const totalCell = (
    value: number | null,
    measureId: string,
    position: TotalPosition,
  ): PivotCell => ({
    measureId,
    value,
    position,
    meaningful: totalIsMeaningful(model, spec, measureId, position),
  });

  // The innermost level is never foldable: a group of one row folded is that
  // row, printed again.
  const lastLevel = spec.rows.length - 1;
  const toggled = new Set(spec.toggledGroups ?? []);
  const collapsedLevels = spec.collapsed ?? [];
  const subtotalled = (level: number) => spec.subtotals.includes(spec.rows[level] ?? '');
  /** A group's state: its level's default, flipped if the reader flipped it. */
  const isCollapsed = (rowKey: DimTuple, level: number): boolean => {
    if (level >= lastLevel) return false;
    const byDefault = collapsedLevels.includes(spec.rows[level] ?? '');
    return toggled.has(groupKey(rowKey.slice(0, level + 1))) !== byDefault;
  };
  /** Enclosing groups, above `level`, that print a total row of their own. */
  const outlineAbove = (rowKey: DimTuple, level: number): number => {
    let depth = 0;
    for (let l = 0; l < Math.min(level, lastLevel); l++) {
      if (subtotalled(l) || isCollapsed(rowKey, l)) depth++;
    }
    return depth;
  };

  /** Does the leaf at `ri` start a new group at `level`? */
  const opens = (ri: number, level: number): boolean => {
    const here = view.rowKeys[ri];
    const previous = view.rowKeys[ri - 1];
    return (
      previous === undefined ||
      here === undefined ||
      !here.slice(0, level + 1).every((v, n) => v.key === previous[n]?.key)
    );
  };
  /**
   * One row-header cell, read as the leaf at `ri` would print it.
   *
   * Blank only when this level AND every level above it repeat — a new parent
   * starts a new group, and its first child must print even if it happens to
   * share a name with the last one. A collapsed group's caption passes the
   * group's FIRST leaf, because that is the row it stands in for.
   */
  const headAt = (rowKey: DimTuple, ri: number, i: number): PivotRowHead => {
    const value = rowKey[i];
    const repeat = spec.suppressRepeats.includes(spec.rows[i] ?? '') && !opens(ri, i);
    const text = memberText(model, spec.rows[i], value);
    return { text: repeat ? '' : text, empty: !repeat && text === '', repeat, key: value?.key };
  };

  const rows: PivotRow[] = [];
  /** Body-row index where the current group at each level began. */
  const groupStart: number[] = [];
  /** Leaf index where the current group at each level began. */
  const groupFirstLeaf: number[] = [];
  /** The group whose toggle each level last drew — one control per group. */
  const announced: (string | undefined)[] = [];
  /**
   * Put the toggle for each group this visible row is the first of, down to
   * `through`. `collapsedAt` is the level folded here, if any.
   */
  const announce = (
    head: PivotRowHead[],
    rowKey: DimTuple,
    through: number,
    collapsedAt?: number,
  ) => {
    for (let i = 0; i <= Math.min(through, lastLevel - 1); i++) {
      const key = groupKey(rowKey.slice(0, i + 1));
      if (announced[i] === key) continue;
      announced[i] = key;
      const cell = head[i];
      if (cell) cell.toggle = { key, collapsed: i === collapsedAt };
    }
  };

  view.rowKeys.forEach((rowKey, ri) => {
    for (let level = 0; level < spec.rows.length; level++) {
      if (opens(ri, level)) {
        groupStart[level] = rows.length;
        groupFirstLeaf[level] = ri;
      }
    }

    // The OUTERMOST folded group this row sits in: the one whose caption is
    // what shows. Anything folded inside it is hidden along with it.
    let folded: number | undefined;
    for (let level = 0; level < lastLevel; level++) {
      if (isCollapsed(rowKey, level)) {
        folded = level;
        break;
      }
    }

    const head = rowKey.map((_value, i) => headAt(rowKey, ri, i));
    if (folded === undefined) announce(head, rowKey, lastLevel);
    rows.push({
      kind: 'value',
      key: rowKey,
      head,
      cells: columns.map((column) =>
        column.isTotalBand
          ? totalCell(view.rowTotal(rowKey, column.measureId), column.measureId, 'rowTotal')
          : {
              measureId: column.measureId,
              value: view.cell(rowKey, column.colKey, column.measureId),
              meaningful: true,
            },
      ),
      outline: outlineAbove(rowKey, lastLevel),
      ...(folded !== undefined && { hidden: true }),
    });

    // A group's total row prints once its last row has, innermost level first —
    // the same order a reader closes them in. A folded group's total is its
    // caption, so it prints whether or not the level asked for subtotals.
    for (const level of closingLevels(view.rowKeys, ri, lastLevel)) {
      const collapsed = isCollapsed(rowKey, level);
      if (!collapsed && !subtotalled(level)) continue;
      const prefix = rowKey.slice(0, level + 1);
      // Hidden when a group OUTSIDE this one is folded. The outermost folded
      // group itself is the row that shows.
      const hidden = folded !== undefined && folded < level;
      const cells = columns.map((column) =>
        column.isTotalBand
          ? // The corner of a subtotal band needs BOTH directions to mean
            // something — which is what `corner` says.
            totalCell(view.subtotalAcross(prefix, column.measureId), column.measureId, 'corner')
          : // A subtotal aggregates down the rows exactly as the bottom total
            // does, so it obeys the same rule.
            totalCell(
              view.subtotal(prefix, column.colKey, column.measureId),
              column.measureId,
              'colTotal',
            ),
      );
      const base = {
        kind: 'subtotal' as const,
        key: prefix,
        level,
        from: groupStart[level] ?? 0,
        cells,
        outline: outlineAbove(rowKey, level),
        ...(hidden && { hidden: true }),
      };

      if (collapsed) {
        // Captioned like the row it replaces — its ancestors, then its own name
        // — and not "… total": on screen it IS the group, folded.
        const first = groupFirstLeaf[level] ?? ri;
        const foldedHead = spec.rows.map(
          (_dim, i): PivotRowHead =>
            i <= level ? headAt(rowKey, first, i) : { text: '', empty: false, repeat: false },
        );
        if (!hidden) announce(foldedHead, rowKey, level, level);
        rows.push({ ...base, collapsed: true, head: foldedHead });
      } else {
        rows.push({
          ...base,
          head: spec.rows.map((_dim, i) => ({
            text: i === level ? `${memberText(model, spec.rows[level], rowKey[level])} total` : '',
            empty: false,
            repeat: false,
          })),
        });
      }
    }
  });

  if (showRowTotal) {
    rows.push({
      kind: 'total',
      from: 0,
      outline: 0,
      head: spec.rows.map((_dim, i) => ({
        // NOT "Total" when the query stopped short. This cell is the one a
        // reader quotes, and over a truncated result it is the total of the
        // groups that happened to come back — a number that looks like the
        // answer and is not. The word is what makes it look like one.
        text: i === 0 ? (options.truncated ? 'Total (partial)' : 'Total') : '',
        empty: false,
        repeat: false,
      })),
      cells: columns.map((column) =>
        column.isTotalBand
          ? totalCell(view.grandTotal(column.measureId), column.measureId, 'grand')
          : totalCell(view.colTotal(column.colKey, column.measureId), column.measureId, 'colTotal'),
      ),
    });
  }

  return {
    rowDimensions: spec.rows,
    headerLevels: headerLevels(model, view, spec, measures.length),
    columns,
    rows,
    showColTotal,
    showRowTotal,
    partial: options.truncated === true,
  };
}

/**
 * A foldable group's identity: its level, and the keys that lead to it.
 *
 * The level is spelled out rather than left to the tuple's length, so a
 * reducer can clear one level's flips without re-deriving the axis — and it is
 * the KEYS, never the labels, so two customers with the same name stay two
 * groups. This string is what `spec.toggledGroups` stores.
 */
export function groupKey(prefix: DimTuple): string {
  return `${prefix.length - 1}:${tupleKey(prefix)}`;
}

/** The level a {@link groupKey} names. */
export function groupLevel(key: string): number {
  return Number.parseInt(key, 10);
}

/**
 * Which row levels close after the row at `ri`.
 *
 * A level closes when the next row differs at it or above, or when there is no
 * next row. Returned innermost-first, because that is the order a reader
 * closes the groups in. The last level is never included: a subtotal of a
 * single row is that row printed twice, and a group of one row cannot fold.
 */
function closingLevels(rowKeys: readonly DimTuple[], ri: number, lastLevel: number): number[] {
  const here = rowKeys[ri];
  const next = rowKeys[ri + 1];
  if (!here) return [];

  const levels: number[] = [];
  for (let level = lastLevel - 1; level >= 0; level--) {
    const closes = !next || !here.slice(0, level + 1).every((v, n) => v.key === next[n]?.key);
    if (closes) levels.push(level);
  }
  return levels;
}

/**
 * The nested column header, one level per column dimension.
 *
 * Built from `view.colKeys` — the combinations that actually came back — rather
 * than the model's declared members, so header spans always line up with the
 * cells beneath them. A dimension whose data is sparse simply has fewer
 * columns, and no phantom header appears over nothing.
 *
 * A cell widens only while CONSECUTIVE columns share its ancestor path, because
 * an HTML `colSpan` cannot cover a gap, and neither can a merged Excel range.
 * `pivot()` guarantees that adjacency by ordering each axis level by level, so
 * siblings always arrive together; the start index is folded into the React key
 * anyway, so a repeated path can never collide into a duplicate key even if
 * that ordering were bypassed.
 */
function headerLevels(
  model: ReportModel,
  view: PivotView,
  spec: ReportSpec,
  measureCount: number,
): PivotHeaderLevel[] {
  return spec.cols.map((dimensionId, level) => {
    const cells: PivotHeaderCell[] = [];
    let current: PivotHeaderCell | null = null;

    view.colKeys.forEach((colKey: DimTuple, index) => {
      const path = colKey
        .slice(0, level + 1)
        .map((v) => v.key ?? '')
        .join(' › ');
      if (current && current.path === path) {
        // Same ancestor path as the previous column: widen rather than repeat.
        current.colSpan += measureCount;
        return;
      }
      current = {
        path,
        start: index,
        member: memberText(model, spec.cols[level], colKey[level]),
        colSpan: measureCount,
      };
      cells.push(current);
    });
    return { dimensionId, cells };
  });
}
